/**
 * FormIO Optimizations - URL Malformation Detection, Caching, and Request Deduplication
 * 
 * This file contains various optimizations for FormIO components including:
 * - Malformed URL detection and cleaning
 * - Request deduplication to prevent multiple identical requests
 * - Client-side caching for select/resource endpoints
 * - Component initialization staggering to reduce server load
 * 
 * DISABLED FOR NOW - These optimizations need review before enabling
 * Created from: /Users/ibrahim/develop/genimind/hic/aeon/aeon-frontend/src/index.jsx lines ~154-498
 */

import { Formio } from '@formio/js';
import { Components } from '@formio/js';
import config from '../config.js';

/**
 * Apply FormIO optimizations and request handling improvements
 * WARNING: This function modifies global FormIO behavior
 */
export function applyFormIOOptimizations() {
    // Fix ChoicesJS dropdown clipping inside overflow:hidden ancestors (cards, tables).
    //
    // APPROACH: Move the dropdown to document.body with position:fixed (correct positioning),
    // then fix the two broken event handlers that assume dropdown is inside containerOuter:
    //
    // 1. _onClick (on document) checks containerOuter.contains(target) — when dropdown is in
    //    body this returns false and every choice click is treated as "outside" → dropdown closes.
    //    Fix: patch _onClick to also treat dropdown-in-body clicks as "inside" clicks.
    //
    // 2. _onMouseDown (on outerElement only) never fires for the detached dropdown.
    //    Fix: add a mousedown listener on the dropdown itself that calls _onMouseDown directly.
    const SelectComponent = Components.components.select;
    if (SelectComponent) {
        const originalAttach = SelectComponent.prototype.attach;
        SelectComponent.prototype.attach = function(element) {
            const result = originalAttach.call(this, element);
            const choices = this.choices;
            if (!choices) return result;

            const dropdown = choices.dropdown && choices.dropdown.element;
            const container = choices.containerOuter && choices.containerOuter.element;
            if (!dropdown || !container) return result;

            // Move to body — this is what gave correct positioning originally
            document.body.appendChild(dropdown);
            dropdown.style.position = 'fixed';
            dropdown.style.zIndex = '9999';
            dropdown.style.margin = '0';

            const reposition = () => {
                if (!choices.dropdown.isActive) return;
                const rect = container.getBoundingClientRect();
                const dropH = dropdown.offsetHeight || 300;
                const spaceBelow = window.innerHeight - rect.bottom;
                dropdown.style.left = rect.left + 'px';
                dropdown.style.width = rect.width + 'px';
                if (spaceBelow < dropH && rect.top >= dropH) {
                    dropdown.style.top = (rect.top - dropH) + 'px';
                } else {
                    dropdown.style.top = rect.bottom + 'px';
                }
            };

            choices.passedElement.element.addEventListener('showDropdown', () => {
                requestAnimationFrame(reposition);
            });
            window.addEventListener('scroll', reposition, true);
            window.addEventListener('resize', reposition);

            // FIX 1: _onMouseDown is bound to outerElement — it never fires for the detached
            // dropdown. Forward mousedown events from the dropdown directly to _onMouseDown.
            const forwardMouseDown = (e) => {
                if (typeof choices._onMouseDown === 'function') {
                    choices._onMouseDown(e);
                }
            };
            dropdown.addEventListener('mousedown', forwardMouseDown, true);

            // FIX 2: _onClick checks containerOuter.contains(target). For a target inside
            // our detached dropdown that check returns false → treated as outside click →
            // dropdown closes without selecting. Patch _onClick to also accept dropdown targets.
            const origOnClick = choices._onClick.bind(choices);
            choices._onClick = function(event) {
                const { target } = event;
                if (dropdown.contains(target)) {
                    // Target is inside our detached dropdown — do nothing here.
                    // forwardMouseDown already handled the selection via _onMouseDown.
                    return;
                }
                origOnClick(event);
            };
            // Re-register the patched handler on document (ChoicesJS bound the original)
            document.removeEventListener('click', origOnClick);
            document.addEventListener('click', choices._onClick, { passive: true });

            const origDestroy = choices.destroy.bind(choices);
            choices.destroy = () => {
                window.removeEventListener('scroll', reposition, true);
                window.removeEventListener('resize', reposition);
                dropdown.removeEventListener('mousedown', forwardMouseDown, true);
                document.removeEventListener('click', choices._onClick);
                if (dropdown.parentNode === document.body) document.body.removeChild(dropdown);
                origDestroy();
            };

            return result;
        };
    }

    // Set global FormIO options to reduce multiple requests
    if (typeof Formio !== 'undefined') {
        // Configure FormIO to use our backend URL
        Formio.setBaseUrl(config.SystemUrl);
        
        // Disable FormIO's internal caching to use our server-side caching
        Formio.cache = {};
        
        // Request deduplication to prevent multiple identical requests
        const pendingRequests = new Map();
        const responseCache = new Map();
        const recentRequests = new Map(); // Very short-term deduplication
        const CACHE_DURATION = 30000; // 30 seconds client-side cache
        const RECENT_REQUEST_WINDOW = 1000; // 1 second window for identical requests
        
        // Configure request handling to include authentication and reduce duplicates
        const origRequest = Formio.request;
        Formio.request = function(url, method, data, header, options) {
            // Ensure header is properly formatted for FormIO
            // FormIO expects headers to be an array of objects with name/value properties
            let formattedHeader = header;
            if (!formattedHeader || !Array.isArray(formattedHeader)) {
                formattedHeader = [];
                
                // Convert object-style headers to FormIO array format
                if (header && typeof header === 'object' && !Array.isArray(header)) {
                    for (const [key, value] of Object.entries(header)) {
                        formattedHeader.push({ name: key, value: value });
                    }
                }
            }
            
            // Ensure authentication header is present
            const authToken = typeof localStorage !== 'undefined' && localStorage.getItem('token');
            if (authToken) {
                // Check if Authorization header already exists
                const hasAuthHeader = formattedHeader.some(h => h.name === 'Authorization');
                if (!hasAuthHeader) {
                    formattedHeader.push({ name: 'Authorization', value: `Bearer ${authToken}` });
                }
            }
            
            // Add Content-Type header if not present and we have data
            const hasContentType = formattedHeader.some(h => h.name === 'Content-Type');
            if (!hasContentType) {
                // For POST/PUT requests with data, use application/json
                if (method && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && data) {
                    formattedHeader.push({ name: 'Content-Type', value: 'application/json' });
                }
            }
            
            // Detect malformed URLs and log them
            const isMalformed = url && (
                url.includes('amp%3B') || 
                url.includes('amp;') || 
                url.includes('&amp;amp;') ||
                url.includes('%3B%3B') ||
                (url.match(/&amp;/g) || []).length > 1
            );
            
            // Try to identify the FormIO component that made this request
            let componentInfo = 'unknown';
            try {
                // Get the component from the call stack or context
                const stack = new Error().stack;
                if (stack) {
                    // Look for FormIO component patterns in stack trace
                    const componentMatch = stack.match(/at\s+(\w*(?:Select|Component|Field|Input))/i);
                    if (componentMatch) {
                        componentInfo = componentMatch[1];
                    }
                }
                
                // Try to get component from options or data context
                if (options && options.component) {
                    componentInfo = `${options.component.type || 'unknown'} (key: ${options.component.key || 'unknown'})`;
                }
            } catch (e) {
                // Silent fallback
            }
            
            if (isMalformed) {
                console.error('🚨 FRONTEND: Malformed URL detected!', {
                    original_url: url,
                    component_info: componentInfo,
                    method: method || 'GET',
                    timestamp: new Date().toISOString(),
                    user_agent: navigator.userAgent,
                    page_url: window.location.href,
                    call_stack: new Error().stack,
                    options: options,
                    data: data
                });
                
                // Send additional info to backend via custom header
                if (!header) header = {};
                header['X-FormIO-Component'] = componentInfo;
                header['X-Malformed-URL-Source'] = 'frontend-formio';
            }
            
            // Normalize URL to prevent encoding issues
            let normalizedUrl;
            try {
                // Fix common URL encoding issues before processing
                let cleanUrl = url;
                
                // Fix malformed amp encoding that's causing backend issues
                if (cleanUrl.includes('amp%3B') || cleanUrl.includes('amp;')) {
                    console.warn('🔧 FRONTEND: Cleaning malformed URL', {
                        original: cleanUrl,
                        component: componentInfo
                    });
                    
                    cleanUrl = cleanUrl.replace(/amp[;&%]+/g, '&');
                    cleanUrl = cleanUrl.replace(/[&]{2,}/g, '&'); // Remove duplicate &
                    cleanUrl = cleanUrl.replace(/[?&]&+/g, '?'); // Clean up leading &
                    
                    console.warn('🔧 FRONTEND: URL cleaned', {
                        cleaned: cleanUrl,
                        component: componentInfo
                    });
                }
                
                normalizedUrl = new URL(cleanUrl, config.BackendUrl).href;
            } catch (e) {
                console.warn('[Formio] URL normalization failed:', url, e);
                normalizedUrl = url;
            }
            
            // Create a unique key for this request - normalize method and data
            const normalizedMethod = (method || 'GET').toUpperCase();
            const normalizedData = JSON.stringify(data || {});
            const reqKey = `${normalizedMethod}-${normalizedUrl}-${normalizedData}`;
            
            // Check for very recent identical requests (within 1 second)
            const now = Date.now();
            const recentRequest = recentRequests.get(reqKey);
            if (recentRequest && (now - recentRequest.timestamp) < RECENT_REQUEST_WINDOW) {
                console.log('[Formio] Blocking very recent duplicate request:', normalizedUrl);
                return recentRequest.promise;
            }
            
            // For GET requests to select endpoints, check cache first
            if (normalizedMethod === 'GET' && normalizedUrl.includes('/admin/select/')) {
                const cached = responseCache.get(reqKey);
                if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
                    console.log('[Formio] Using cached response:', normalizedUrl);
                    return Promise.resolve(cached.response);
                }
            }
            
            // If we already have a pending request, return that promise
            if (pendingRequests.has(reqKey)) {
                console.log('[Formio] Deduplicating request:', normalizedUrl);
                return pendingRequests.get(reqKey);
            }
            
            // Ensure proper authentication headers
            header = header || {};
            
            // Add authentication token from localStorage
            const token = localStorage.getItem('token');
            if (token) {
                header['Authorization'] = `Bearer ${token}`;
            }
            
            // Add language header
            const lang = localStorage.getItem('lang') || 'en';
            header['lang'] = lang;
            
            // Add content type and accept headers
            if (!header['Accept']) {
                header['Accept'] = 'application/json';
            }
            if (!header['Content-Type'] && data) {
                header['Content-Type'] = 'application/json';
            }
            
            // Add CSRF token from meta tag if available
            const csrfToken = document.querySelector('meta[name="csrf-token"]');
            if (csrfToken) {
                header['X-CSRF-TOKEN'] = csrfToken.getAttribute('content');
            }
            
            console.log('[Formio] Making request:', { 
                method: normalizedMethod, 
                url: normalizedUrl, 
                component: componentInfo,
                is_malformed: isMalformed,
                headers: formattedHeader 
            });
            
            // Make the request and store the promise
            const requestPromise = origRequest.call(this, normalizedUrl, method, data, formattedHeader, { 
                ...options, 
                credentials: 'include',
                withCredentials: true 
            }).then(response => {
                // Log successful response for malformed URL tracking
                if (normalizedMethod === 'GET' && normalizedUrl.includes('/admin/select/')) {
                    console.log('[Formio] Select request completed:', {
                        url: normalizedUrl,
                        component: componentInfo,
                        was_malformed: isMalformed,
                        response_size: JSON.stringify(response).length
                    });
                    
                    // Cache the response
                    responseCache.set(reqKey, {
                        response: response,
                        timestamp: Date.now()
                    });
                }
                return response;
            }).finally(() => {
                // Remove from pending requests when done
                pendingRequests.delete(reqKey);
                
                // Clean up old recent requests
                const cutoff = Date.now() - RECENT_REQUEST_WINDOW;
                for (const [key, value] of recentRequests.entries()) {
                    if (value.timestamp < cutoff) {
                        recentRequests.delete(key);
                    }
                }
            }).catch(error => {
                if (isMalformed) {
                    console.error('💥 FRONTEND: Malformed URL request failed:', { 
                        url: normalizedUrl, 
                        component: componentInfo,
                        error: error.message || error 
                    });
                }
                console.error('[Formio] Request failed:', { url: normalizedUrl, error });
                throw error;
            });
            
            // Store in pending requests for deduplication
            pendingRequests.set(reqKey, requestPromise);
            
            // Store in recent requests for very short-term deduplication
            recentRequests.set(reqKey, {
                promise: requestPromise,
                timestamp: now
            });
            
            return requestPromise;
        };
        
        // Set up global FormIO options to prevent multiple requests
        Formio.options = {
            ...Formio.options,
            template: 'bootstrap',
            noAlerts: false,
            language: localStorage.getItem('lang') || 'en',
            i18n: {},
            // Add delay to prevent simultaneous requests during page load
            componentDelay: 100  // 100ms delay between component initializations
        };
        
        // Override FormIO component loading to stagger initialization and add tracking
        const origCreateComponent = Formio.createComponent;
        if (origCreateComponent) {
            let componentInitDelay = 0;
            Formio.createComponent = function(component, options, data) {
                // Log component creation for select/resource components
                if (component && component.type && ['select', 'resource'].includes(component.type)) {
                    console.log('🔧 FRONTEND: Creating FormIO select component:', {
                        type: component.type,
                        key: component.key,
                        label: component.label,
                        dataSrc: component.dataSrc,
                        url: component.data?.url,
                        timestamp: new Date().toISOString()
                    });
                    
                    // Check if the component has a malformed URL
                    if (component.data?.url && (
                        component.data.url.includes('amp%3B') || 
                        component.data.url.includes('amp;') || 
                        component.data.url.includes('&amp;amp;')
                    )) {
                        console.error('🚨 FRONTEND: Component has malformed URL!', {
                            component_key: component.key,
                            component_label: component.label,
                            malformed_url: component.data.url,
                            component_full: component
                        });
                    }
                    
                    componentInitDelay += 50; // 50ms delay between select components
                    const currentDelay = componentInitDelay;
                    
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            const createdComponent = origCreateComponent.call(this, component, options, data);
                            
                            // Add tracking to the created component
                            if (createdComponent && typeof createdComponent.then === 'function') {
                                createdComponent.then(comp => {
                                    if (comp && comp.loadItems) {
                                        const origLoadItems = comp.loadItems.bind(comp);
                                        comp.loadItems = function(url, search, options) {
                                            console.log('📡 FRONTEND: Component loading items:', {
                                                component_key: component.key,
                                                component_type: component.type,
                                                url: url,
                                                search: search
                                            });
                                            return origLoadItems(url, search, options);
                                        };
                                    }
                                });
                            }
                            
                            resolve(createdComponent);
                        }, currentDelay);
                    });
                }
                
                // For non-select components, use original behavior
                return origCreateComponent.call(this, component, options, data);
            };
        }
        
        // Clear client-side cache on page navigation
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                pendingRequests.clear();
                responseCache.clear();
                recentRequests.clear();
            });
            
            // Also clear cache on hash/route changes (for SPA navigation)
            window.addEventListener('hashchange', () => {
                console.log('[Formio] Route change detected, clearing cache');
                pendingRequests.clear();
                responseCache.clear();
                recentRequests.clear();
            });
        }
    }
}

export default applyFormIOOptimizations;